import React from 'react';
import { 
  Database, 
  Brain, 
  Cpu, 
  Layers, 
  Workflow, 
  ShieldCheck, 
  Server, 
  BarChart3 
} from 'lucide-react';
import { NavItem, ServiceItem } from './types';

export const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Solutions', path: '/solutions' },
  { label: 'Technology', path: '/technology' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export const SERVICES: ServiceItem[] = [
  {
    id: 'data-eng',
    slug: 'data-engineering',
    title: 'Data Engineering & Infrastructure',
    description: 'Cloud-native data platforms that scale. From lakehouse design to real-time pipelines—built for AI readiness and governance.',
    outcome: 'Faster time-to-insight, AI-ready data',
    icon: <Database className="w-6 h-6" />,
    features: ['Batch & streaming pipelines', 'Lakehouse & data mesh', 'Data quality & lineage'],
    detail: {
      intro: 'Enterprises today need a single source of truth that supports both analytics and AI. We design and build data platforms that scale with your business and comply with modern regulations.',
      whatWeDeliver: ['Cloud data warehouse and lakehouse design (Snowflake, Databricks, BigQuery)', 'Real-time and batch ingestion with dbt, Airflow, and Fivetran', 'Data quality, lineage, and catalog for governance and AI readiness', 'Migration from legacy EDW to modern stack'],
      benefits: ['Unified data foundation for BI and AI', 'Reduced data latency and duplication', 'Clear ownership and lineage for compliance', 'Lower TCO than legacy infrastructure'],
      ctaLine: 'Ready to modernise your data foundation?',
    },
  },
  {
    id: 'data-science',
    slug: 'data-science-analytics',
    title: 'Data Science & Analytics',
    description: 'Turn data into decisions. Production forecasting, anomaly detection, and KPI models that drive revenue and risk management.',
    outcome: 'Actionable insights at scale',
    icon: <BarChart3 className="w-6 h-6" />,
    features: ['Forecasting & planning', 'Anomaly detection', 'KPI & attribution modeling'],
    detail: {
      intro: 'We build data science solutions that run in production—not just in notebooks. From demand forecasting to fraud detection, our models are deployed with the same rigour as your core systems.',
      whatWeDeliver: ['Demand and revenue forecasting with confidence intervals', 'Real-time anomaly detection for ops and security', 'Attribution and LTV models for marketing and product', 'Dashboards and alerts that stakeholders actually use'],
      benefits: ['Faster, data-driven decisions', 'Fewer false alarms and missed issues', 'Repeatable, versioned models in production', 'Clear ROI through measurable outcomes'],
      ctaLine: 'Want production-grade analytics, not one-off reports?',
    },
  },
  {
    id: 'ml-ai',
    slug: 'ml-applied-ai',
    title: 'ML Modeling & Applied AI',
    description: 'Classical ML and deep learning for NLP, vision, and recommendation—deployed for real-time inference and scale.',
    outcome: 'ML that ships and scales',
    icon: <Brain className="w-6 h-6" />,
    features: ['NLP & document AI', 'Computer vision', 'Recommendation & search'],
    detail: {
      intro: 'We design and deploy ML systems that move the needle: search and recommendations, document understanding, and vision applications—all with proper evaluation, monitoring, and governance.',
      whatWeDeliver: ['Custom models for classification, NER, and document understanding', 'Recommendation and search systems with A/B testing', 'Computer vision for quality, safety, and automation', 'Real-time inference APIs and feature stores'],
      benefits: ['Models that perform in production, not just in experiments', 'Reduced bias and drift with monitoring', 'Faster iteration with MLOps and feature reuse', 'Alignment with EU AI Act and internal policies'],
      ctaLine: 'Need ML that goes from pilot to production?',
    },
  },
  {
    id: 'gen-ai',
    slug: 'generative-ai-agents',
    title: 'Generative AI & Agents',
    description: 'RAG, copilots, and agentic workflows. Enterprise-grade GenAI with your data, your models, and your guardrails.',
    outcome: 'GenAI you can trust and scale',
    icon: <Cpu className="w-6 h-6" />,
    features: ['RAG & knowledge assistants', 'Copilots & agents', 'Private LLMs & fine-tuning'],
    detail: {
      intro: 'Generative AI is reshaping how enterprises work. We build RAG systems, copilots, and agent workflows that use your data safely—with proper grounding, guardrails, and cost control.',
      whatWeDeliver: ['RAG pipelines with your docs, wikis, and databases', 'Internal copilots for support, sales, and operations', 'Multi-step agents with tools and human-in-the-loop', 'Private and fine-tuned models where compliance requires it'],
      benefits: ['Higher productivity with accurate, cited answers', 'Reduced hallucination and leakage via design', 'Scalable usage and cost monitoring', 'EU AI Act–aware risk and transparency'],
      ctaLine: 'Ready to deploy GenAI that fits your enterprise?',
    },
  },
  {
    id: 'mlops',
    slug: 'mlops-governance',
    title: 'MLOps & Governance',
    description: 'CI/CD for ML, drift detection, and AI Act readiness. Ship and monitor models with the same discipline as software.',
    outcome: 'Reliable, auditable AI in production',
    icon: <ShieldCheck className="w-6 h-6" />,
    features: ['CI/CD for ML', 'Drift & performance monitoring', 'AI Act readiness'],
    detail: {
      intro: 'MLOps and governance ensure your AI systems stay reliable, fair, and compliant. We implement pipelines, monitoring, and documentation so you can scale with confidence.',
      whatWeDeliver: ['Model registry, versioning, and automated deployment pipelines', 'Drift detection, performance dashboards, and alerting', 'Bias and fairness checks with audit trails', 'AI Act readiness: risk classification, documentation, and human oversight'],
      benefits: ['Fewer production incidents and rollbacks', 'Clear accountability and auditability', 'Faster, safer model updates', 'Readiness for regulatory and customer assurance'],
      ctaLine: 'Need to harden your AI for production and compliance?',
    },
  },
  {
    id: 'automation',
    slug: 'intelligent-automation',
    title: 'Intelligent Automation',
    description: 'AI-driven workflows that connect systems and people. Event-driven automation with human-in-the-loop where it matters.',
    outcome: 'Smarter processes, fewer manual steps',
    icon: <Workflow className="w-6 h-6" />,
    features: ['Event-driven workflows', 'Human-in-the-loop', 'ERP & system integration'],
    detail: {
      intro: 'We design intelligent automation that fits your processes: event-driven workflows, document and task routing, and integrations with ERP and CRM—so your team focuses on exceptions and strategy.',
      whatWeDeliver: ['Workflow orchestration with triggers, conditions, and approvals', 'Document classification, extraction, and routing', 'Integrations with SAP, Salesforce, ServiceNow, and custom APIs', 'Human-in-the-loop design for high-stakes decisions'],
      benefits: ['Faster cycle times and fewer errors', 'Scalable operations without proportional headcount', 'Clear audit trail for compliance', 'Gradual rollout with fallbacks and overrides'],
      ctaLine: 'Want to automate without losing control?',
    },
  },
];
